/**
 * CanvasHandoffCTAs — final-stage hand-off bar at the bottom of the
 * artifact canvas. Per F1-PLAN §6.6 + §13, a Sales-Selection workflow ends
 * at an existing Insights tool — Ion is a funnel, not a dead-end. The one
 * exit here is Compare (the classic-wizard "Skip to Cell Selection" escape
 * hatch lives on the breadcrumb; the real PDF export lives in the Report tab).
 *
 *   ┌──────────────────────┐
 *   │   [Open in Compare]   │
 *   └──────────────────────┘
 *
 * Visibility rules:
 *   - salesModeActive            (otherwise it's just normal Ion chat)
 *   - selectionContext.stage is 'shortlist' or 'trade_offs'  (a final
 *                                                  shortlist exists → surface
 *                                                  the exit immediately;
 *                                                  Ion is a funnel. Nothing
 *                                                  ever set 'trade_offs' on its
 *                                                  own, so gating on it alone
 *                                                  left the bar unreachable.)
 *   - pinned artifact is a ranked_cells_table     (we read rows from there)
 *   - rows.length >= 2                            (need at least 2 cells
 *                                                  to make Compare useful)
 *
 * Compare exit:
 *   - Resolves each row's (manufacturer, name) to a URL slug via the
 *     canonical cellSlugMap.
 *   - Navigates to /compare?cells=slug1,slug2,...  (router parses the
 *     param and seeds compareCells; see core/router.jsx).
 */

import { useLocation } from 'wouter-preact'

import {
  pinnedArtifact,
  salesModeActive,
  selectionContext,
  setSelectionContext,
  manualPicks,
} from '../../../state/chatStore.js'
import { generateSlugFromCell } from '../../../utils/cellSlugMap.js'
import { t } from '../../../i18n/index.js'

const MAX_COMPARE_CELLS = 5

/** Pull cell refs out of the pinned ranked_cells_table artifact. Returns
 *  [] if the artifact isn't a ranked_cells_table or rows is malformed. */
function rankedCellsFromArtifact(artifact) {
  if (!artifact || artifact.type !== 'ranked_cells_table') return []
  const rows = artifact.data?.rows
  if (!Array.isArray(rows)) return []
  return rows.filter((r) => r && r.manufacturer && r.name)
}

/**
 * Merge the user's manual picks ahead of the ranked rows, de-duped by
 * manufacturer::name. Manual picks lead so the max-5 compare cap never drops a
 * cell the user explicitly added. Pure — exported for tests.
 */
function mergeManualPicksForHandoff(picks, rankedRows) {
  const manual = (Array.isArray(picks) ? picks : [])
    .filter((p) => p && p.manufacturer && p.name)
    .map((p) => ({ manufacturer: p.manufacturer, name: p.name }))
  const seen = new Set()
  const out = []
  for (const r of [...manual, ...(Array.isArray(rankedRows) ? rankedRows : [])]) {
    const id = `${r.manufacturer}::${r.name}`
    if (seen.has(id)) continue
    seen.add(id)
    out.push(r)
  }
  return out
}

export function CanvasHandoffCTAs() {
  if (!salesModeActive.value) return null
  const stage = selectionContext.value.stage
  if (stage !== 'shortlist' && stage !== 'trade_offs') return null

  const artifact = pinnedArtifact.value
  const rankedRows = rankedCellsFromArtifact(artifact)
  // The user's manual picks (incl. cells Ion ruled out) come FIRST so the
  // max-5 compare cap never silently drops a cell they explicitly chose.
  const rows = mergeManualPicksForHandoff(manualPicks.value, rankedRows)
  if (rows.length < 2) return null

  const [, navigate] = useLocation()

  async function onOpenInCompare() {
    const slugs = rows
      .slice(0, MAX_COMPARE_CELLS)
      .map((r) => generateSlugFromCell(r))
      .filter(Boolean)

    if (slugs.length < 2) {
      console.warn('[CanvasHandoffCTAs] not enough resolvable slugs for compare')
      return
    }

    // Opening a comparison IS the trade_offs step — complete the breadcrumb
    // before leaving so it reads correctly if the user returns to /assistant.
    setSelectionContext({ stage: 'trade_offs' })
    // Side-effect free: router parses ?cells= and seeds compareCells.
    navigate(`/compare?cells=${slugs.join(',')}`)
  }

  return (
    <footer class="ion-handoff-ctas" role="group" aria-label="Sales-Selection hand-off">
      <button
        type="button"
        class="ion-handoff-ctas__btn ion-handoff-ctas__btn--primary"
        onClick={onOpenInCompare}
        aria-label={t('ion.workflow.handoff.compare.aria')}
      >
        {t('ion.workflow.handoff.compare')} →
      </button>
    </footer>
  )
}

// Exported for tests.
export const __test__ = { rankedCellsFromArtifact, mergeManualPicksForHandoff }
